import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getLanguages, translate as apiTranslate } from "@/lib/api";

const LanguageContext = createContext(null);
export const useLanguage = () => useContext(LanguageContext);

const LS_KEY = "usapulse_lang";

export function LanguageProvider({ children }) {
  const [languages, setLanguages] = useState([]);
  const [lang, setLangState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY)) || { code: "en", name: "English", direction: "ltr" };
    } catch {
      return { code: "en", name: "English", direction: "ltr" };
    }
  });
  const [dict, setDict] = useState({}); // text -> translated
  const [translating, setTranslating] = useState(false);

  const registry = useRef(new Set()); // all known source strings
  const pending = useRef(new Set());
  const flushTimer = useRef(null);

  useEffect(() => {
    getLanguages().then((d) => setLanguages(d.languages)).catch(() => {});
  }, []);

  // apply dir + lang to <html>
  useEffect(() => {
    document.documentElement.setAttribute("lang", lang.code);
    document.documentElement.setAttribute("dir", lang.direction || "ltr");
    localStorage.setItem(LS_KEY, JSON.stringify(lang));
  }, [lang]);

  const runBatch = useCallback(async (items, targetLang) => {
    if (!items.length || targetLang.code === "en") return;
    setTranslating(true);
    try {
      const res = await apiTranslate(items, targetLang.code, targetLang.name);
      setDict((prev) => {
        const next = { ...prev };
        items.forEach((t, i) => { next[t] = res.translations[i] ?? t; });
        return next;
      });
    } catch {
      /* keep source on failure */
    } finally {
      setTranslating(false);
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => {
      if (lang.code === "en") { pending.current.clear(); return; }
      const items = Array.from(pending.current).filter((t) => !(t in dict));
      pending.current.clear();
      if (items.length) runBatch(items, lang);
    }, 250);
  }, [lang, dict, runBatch]);

  // register a source string; returns translated (or source) text
  const register = useCallback((text) => {
    if (!text || typeof text !== "string") return text;
    if (!registry.current.has(text)) registry.current.add(text);
    if (lang.code !== "en" && !(text in dict)) {
      pending.current.add(text);
      scheduleFlush();
    }
    return lang.code === "en" ? text : (dict[text] ?? text);
  }, [lang, dict, scheduleFlush]);

  // when language changes, translate everything we know about
  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    if (newLang.code !== "en") {
      const items = Array.from(registry.current);
      // chunk to keep requests reasonable
      const chunkSize = 40;
      for (let i = 0; i < items.length; i += chunkSize) {
        runBatch(items.slice(i, i + chunkSize), newLang);
      }
    } else {
      setDict({});
    }
  }, [runBatch]);

  const value = { languages, lang, setLang, register, translating, dict };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// Translatable text component — reads context directly so it re-renders on dict/lang change
export function T({ children }) {
  const ctx = useLanguage();
  const text = typeof children === "string" ? children : String(children ?? "");

  useEffect(() => {
    if (ctx) ctx.register(text);
  }, [ctx, text, ctx?.lang.code]);

  if (!ctx || ctx.lang.code === "en") return <>{text}</>;
  return <>{ctx.dict[text] ?? text}</>;
}

// hook returning a plain translate function for imperative strings
export function useT() {
  const ctx = useLanguage();
  return (text) => (ctx ? ctx.register(text) : text);
}
